//
// Copyright (c) 2011-2019 by Curalate, Inc.
//

import React from 'react';
import styled from 'styled-components';

import {
  IContentItemSocialDetails,
  IContentLibraryItem,
} from '@apis/content-library/contentLibraryApiModel';
import Avatar from '@components/atoms/avatar/Avatar';
import { Caption } from '@components/atoms/caption/Caption';
import Icon from '@components/atoms/icon/Icon';
import {
  BodyBoldText,
  CaptionSecondaryText,
} from '@components/atoms/text/Text';
import { MediumTooltipForIcon } from '@components/molecules/tooltip/Tooltip';
import { Mixins } from '@theme/mixins';

interface Props {
  contentItem: IContentLibraryItem;
  className?: string;
}

const getProfileImage = (socialDetails?: IContentItemSocialDetails) => {
  return socialDetails?.user?.profilePicture;
};

const getUsername = (socialDetails?: IContentItemSocialDetails) => {
  return socialDetails?.user?.username;
};

export const InfoOverlay = ({ contentItem, className }: Props) => {
  const caption =
    contentItem.socialDetails && contentItem.socialDetails.caption;
  const postLocation =
    contentItem.socialDetails && contentItem.socialDetails.link;
  const username = getUsername(contentItem.socialDetails);

  return (
    <IconContainer className={className}>
      <MediumTooltipForIcon
        target={<InfoIcon name='alarm' />}
        content={
          <ContentContainer>
            <CaptionHeader>
              <ProfileIcon
                imageUrl={getProfileImage(contentItem.socialDetails)}
                username={username}
              />
              <CaptionAuthor>{username}</CaptionAuthor>
              {postLocation && (
                <a href={postLocation} target='_blank'>
                  <LinkoutIcon name='linkout' />
                </a>
              )}
            </CaptionHeader>
            {caption ? (
              <CaptionContainer>
                <Caption caption={caption} />
              </CaptionContainer>
            ) : (
              <CaptionSecondaryText>No caption posted</CaptionSecondaryText>
            )}
          </ContentContainer>
        }
      />
    </IconContainer>
  );
};

const ProfileIcon = styled(Avatar).attrs({ size: 38 })`
  ${({ theme }) => `
    margin-right: ${theme.spacings[2]};
  `}
`;

const LinkoutIcon = styled(Icon)`
  && {
    color: ${({ theme }) => theme.colors.gray2};
  }
`;

const ContentContainer = styled.div`
  padding: ${({ theme }) => theme.spacings[2]};
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  max-width: ${({ theme }) => theme.smallMeasure};
  max-height: ${({ theme }) => theme.smallMeasure};
`;

const CaptionHeader = styled.div`
  display: flex;
  align-items: center;
`;

const CaptionAuthor = styled(BodyBoldText)(
  ({ theme }) => `
  margin-right: ${theme.spacings[2]}
`,
);

const CaptionContainer = styled.div`
  display: inline-block;
  word-break: break-word;
`;

const InfoIcon = styled(Icon)`
  && {
    color: ${({ theme }) => theme.colors.white};
    transform: rotate(180deg);
  }
`;

const IconContainer = styled.div`
  position: absolute;
  ${({ theme }) => `
    right: ${theme.spacings[2]};
    top: ${theme.spacings[2]};
  `}
`;
